import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Tables } from '@/types/database';

/**
 * Per-user Google Calendar overlay for the team Calendar page
 * (migration 83).
 *
 * Model: each user can link their OWN Google account (calendar.readonly).
 * The refresh token is stored server-side (encrypted, integration_secrets);
 * the `google-calendar-events` Edge Function exchanges it per request and
 * proxies the user's primary-calendar events back. Nothing is persisted —
 * it's a live read-only overlay, same idea as the project-tasks projection.
 *
 * OAuth flow mirrors GA4 (see ga4OAuth.ts): client redirects to Google with
 * a CSRF state in sessionStorage → Google redirects to
 * /calendar/google/callback → GoogleCalendarCallbackPage forwards the code
 * to the `google-calendar-oauth-callback` Edge Function.
 */

// ---------------------------------------------------------------------------
// OAuth kickoff
// ---------------------------------------------------------------------------

export const GCAL_OAUTH_STATE_KEY = 'gcal_oauth_state';

export const GCAL_REDIRECT_PATH = '/calendar/google/callback';

// calendar.readonly only — Atlas never writes to the user's Google
// Calendar, and the consent screen stays minimal.
export const GCAL_OAUTH_SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
] as const;

/**
 * Must be registered in the Google Cloud Console under "Authorized
 * redirect URIs" for the same OAuth client as VITE_GOOGLE_CLIENT_ID, and
 * must match exactly between the auth request and the token exchange.
 */
export function gcalRedirectUri(): string {
  return `${window.location.origin}${GCAL_REDIRECT_PATH}`;
}

/**
 * Begins the OAuth flow by redirecting the browser to Google. Returns
 * `null` on success (the tab is about to navigate away) and a
 * human-readable error string if the env isn't configured.
 *
 * `prompt=consent` + `access_type=offline` guarantee Google returns a
 * refresh token even if the user approved this client+scope before —
 * see ga4OAuth.ts for the full rationale.
 */
export function startGoogleCalendarOAuth(): string | null {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    return 'VITE_GOOGLE_CLIENT_ID is not set. Add it to .env (it must match the GOOGLE_CLIENT_ID server secret) and redeploy.';
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(GCAL_OAUTH_STATE_KEY, state);

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', GCAL_OAUTH_SCOPES.join(' '));
  url.searchParams.set('redirect_uri', gcalRedirectUri());
  url.searchParams.set('state', state);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');
  url.searchParams.set('include_granted_scopes', 'true');
  window.location.href = url.toString();
  return null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GoogleCalendarConnection = Tables<'calendar_google_connections'>;

/** Raw event shape returned by the google-calendar-events Edge Function. */
interface RawGoogleEvent {
  id: string;
  summary?: string;
  location?: string;
  htmlLink?: string;
  start?: { date?: string; dateTime?: string };
  end?: { date?: string; dateTime?: string };
}

/**
 * One rendered chip on one day. Multi-day all-day events are expanded
 * into one occurrence per visible day, so the page can group purely by
 * `dateKey` like it does for native events and tasks.
 */
export interface GoogleCalendarOccurrence {
  /** Unique per chip — `${eventId}:${dateKey}`. */
  key: string;
  eventId: string;
  title: string;
  /** Local YYYY-MM-DD the chip renders on. */
  dateKey: string;
  allDay: boolean;
  /** Local 'HH:MM' for timed events, null for all-day. */
  startTime: string | null;
  location: string | null;
  htmlLink: string | null;
}

// ---------------------------------------------------------------------------
// Query keys — deliberately NOT under calendarKeys.all (['calendar']) so
// native-event mutations don't refetch the Google API on every save.
// ---------------------------------------------------------------------------

export const googleCalendarKeys = {
  all: ['calendar-google'] as const,
  connection: (profileId: string) =>
    [...googleCalendarKeys.all, 'connection', profileId] as const,
  events: (profileId: string, range: { start: string; end: string }) =>
    [...googleCalendarKeys.all, 'events', profileId, range] as const,
};

// ---------------------------------------------------------------------------
// Local-date helpers (same LOCAL YYYY-MM-DD convention as CalendarPage)
// ---------------------------------------------------------------------------

function toLocalKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function keyToLocalDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function nextDayKey(key: string): string {
  const d = keyToLocalDate(key);
  d.setDate(d.getDate() + 1);
  return toLocalKey(d);
}

function expandOccurrences(
  ev: RawGoogleEvent,
  range: { start: string; end: string },
): GoogleCalendarOccurrence[] {
  const base = {
    eventId: ev.id,
    title: ev.summary?.trim() || '(No title)',
    location: ev.location ?? null,
    htmlLink: ev.htmlLink ?? null,
  };

  if (ev.start?.dateTime) {
    // Timed event — Google sends an RFC3339 timestamp with offset; the
    // Date constructor converts it to the browser's local time.
    const start = new Date(ev.start.dateTime);
    if (Number.isNaN(start.getTime())) return [];
    const dateKey = toLocalKey(start);
    if (dateKey < range.start || dateKey > range.end) return [];
    const hh = `${start.getHours()}`.padStart(2, '0');
    const mm = `${start.getMinutes()}`.padStart(2, '0');
    return [
      {
        ...base,
        key: `${ev.id}:${dateKey}`,
        dateKey,
        allDay: false,
        startTime: `${hh}:${mm}`,
      },
    ];
  }

  if (ev.start?.date) {
    // All-day event — `end.date` is EXCLUSIVE per the Calendar API.
    // Expand each covered day, clamped to the visible range. Guard
    // against a missing/equal end by covering at least the start day.
    const out: GoogleCalendarOccurrence[] = [];
    const endKeyExclusive =
      ev.end?.date && ev.end.date > ev.start.date
        ? ev.end.date
        : nextDayKey(ev.start.date);
    const cursor = keyToLocalDate(
      ev.start.date > range.start ? ev.start.date : range.start,
    );
    let key = toLocalKey(cursor);
    while (key < endKeyExclusive && key <= range.end) {
      out.push({
        ...base,
        key: `${ev.id}:${key}`,
        dateKey: key,
        allDay: true,
        startTime: null,
      });
      cursor.setDate(cursor.getDate() + 1);
      key = toLocalKey(cursor);
    }
    return out;
  }

  return [];
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** The current user's Google Calendar connection row, if any. */
export function useGoogleCalendarConnection() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: googleCalendarKeys.connection(profile?.id ?? 'noop'),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_google_connections')
        .select('profile_id, organization_id, google_email, status, last_error, connected_at, updated_at')
        .eq('profile_id', profile!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as GoogleCalendarConnection | null) ?? null;
    },
  });
}

/**
 * The user's Google events for the visible grid, already expanded into
 * per-day occurrences in the browser's timezone.
 *
 * A 409 (`reconnect_required`) from the Edge Function means the grant was
 * revoked — we refetch the connection row (now status='error') so the UI
 * offers "Reconnect", and render no overlay rather than erroring the page.
 */
export function useGoogleCalendarEvents(
  range: { start: string; end: string },
  enabled: boolean,
) {
  const { profile } = useAuth();
  const qc = useQueryClient();
  return useQuery({
    queryKey: googleCalendarKeys.events(profile?.id ?? 'noop', range),
    enabled: !!profile && enabled,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      // Window = [local midnight of grid start, local midnight after grid
      // end) so timed events on the boundary days are included regardless
      // of the user's timezone.
      const timeMin = keyToLocalDate(range.start);
      const timeMax = keyToLocalDate(range.end);
      timeMax.setDate(timeMax.getDate() + 1);

      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        events: RawGoogleEvent[];
        error?: string;
        code?: string;
      }>('google-calendar-events', {
        body: {
          time_min: timeMin.toISOString(),
          time_max: timeMax.toISOString(),
        },
      });

      if (error) {
        const status = (error as { context?: Response }).context?.status;
        if (status === 409) {
          void qc.invalidateQueries({
            queryKey: googleCalendarKeys.connection(profile!.id),
          });
          return [];
        }
        throw error;
      }

      const occurrences = (data?.events ?? []).flatMap((ev) =>
        expandOccurrences(ev, range),
      );
      // All-day chips first, then by start time — matches how the native
      // event query orders (nullsFirst on start_time).
      occurrences.sort((a, b) =>
        (a.startTime ?? '').localeCompare(b.startTime ?? ''),
      );
      return occurrences;
    },
  });
}

export function useDisconnectGoogleCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        error?: string;
      }>('google-calendar-disconnect', { body: {} });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? 'Disconnect failed');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: googleCalendarKeys.all });
    },
  });
}
